import {TextHero, TextBody} from '@tkww/orion-web-ds-text';
import {GridRow, GridColumn} from '@tkww/orion-web-ds-structure';
import {compose, useClasses} from '@tkww/orion-web-lib-css-modules';
import {Link} from '@tkww/orion-web-ds-action';
import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import NavLink from './components/NavLink';
import Summary from './components/Summary';
import {formatAsCurrency, getTrackingSource} from './utils';

import style from './style.scss';
import noop from '../../utils/noop';

/*
  Component that was migrated over from fe-shared-components (as of 2024/04/11)
*/
const YourRegistryHeaderBase = ({
  children = null,
  classes = compose({}),
  guestViewUrl,
  location,
  onClickGuestViewUrl = noop,
  onNavigate = noop,
  ownedRoutes = null,
  renderOwnedLink = null,
  routes,
  storeCreditBalance = null,
  tkrsGCBalance = null,
}) => {
  const {pathname} = location;
  const pathEnding = pathname.split('/').pop();
  const hasStoreCredit = storeCreditBalance > 0;
  const hasTKRSGCBalance = tkrsGCBalance > 0;
  const theClasses = useClasses(style, classes);
  const hasOnlyStoreCreditOrGCBalance = !hasStoreCredit || !hasTKRSGCBalance;

  return (
    <>
      <GridRow as="header" data-testid="your-registry-header">
        <GridColumn
          className={cx([
            style.header,
            (hasStoreCredit || hasTKRSGCBalance) && style['has-store-credit'],
          ])}
          xs="12">
          <>
            <TextHero size="md" className={style.title}>
              Registry
            </TextHero>
            <Link
              href={guestViewUrl}
              styleType="secondary"
              size="md"
              iconName="link-out"
              labelDirection="left"
              data-testid="view-as-guest-link-mobile"
              className={style['view-as-guest-link-mobile']}
              onClick={e =>
                onClickGuestViewUrl({source: getTrackingSource(pathEnding)}, e)
              }>
              <TextBody size="md">View as guest</TextBody>
            </Link>
            <div className={style['user-data-content']}>
              <div
                className={cx([
                  style['has-sc-and-gcs'],
                  hasOnlyStoreCreditOrGCBalance && style['has-only-sc-or-gc'],
                ])}>
                {hasTKRSGCBalance && (
                  <TextBody
                    size="sm"
                    className={style['store-credit']}
                    data-testid="gc-balance">
                    Gift card balance:{' '}
                    <span>{formatAsCurrency(tkrsGCBalance)}</span>
                  </TextBody>
                )}
                {hasStoreCredit && (
                  <TextBody
                    size="sm"
                    className={style['store-credit']}
                    data-testid="store-credit">
                    Store credit:{' '}
                    <span>{formatAsCurrency(storeCreditBalance)}</span>
                  </TextBody>
                )}
              </div>
              <Summary />
              <Link
                href={guestViewUrl}
                styleType="secondary"
                size="md"
                iconName="link-out"
                labelDirection="left"
                data-testid="view-as-guest-link-desktop"
                className={style['view-as-guest-link-desktop']}
                onClick={e =>
                  onClickGuestViewUrl(
                    {source: getTrackingSource(pathEnding)},
                    e,
                  )
                }>
                <TextBody size="md">View as guest</TextBody>
              </Link>
            </div>
          </>
        </GridColumn>
      </GridRow>
      <GridRow hGutters={false}>
        <GridColumn xs="12">
          <nav>
            <ul className={style['links-container']} key="registry-header-base">
              {routes.map(
                ({routeName, isHidden, children: routeChildren}) =>
                  !isHidden && (
                    <NavLink
                      key={routeName}
                      routeName={routeName}
                      ownedRoutes={ownedRoutes}
                      renderOwnedLink={renderOwnedLink}
                      currentPath={pathname}
                      onNavigate={onNavigate}
                      activeClassName={style['link-active']}
                      className={style.link}>
                      {routeChildren || routeName}
                    </NavLink>
                  ),
              )}
            </ul>
          </nav>
        </GridColumn>
      </GridRow>
      <GridRow classes={compose(theClasses)}>
        <GridColumn xs="12" className={style.divider} />
      </GridRow>
      {children}
    </>
  );
};

YourRegistryHeaderBase.propTypes = {
  children: PropTypes.node,
  classes: PropTypes.shape({}),
  guestViewUrl: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  onClickGuestViewUrl: PropTypes.func,
  onNavigate: PropTypes.func,
  ownedRoutes: PropTypes.instanceOf(RegExp),
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      routeName: PropTypes.string,
      isHidden: PropTypes.bool,
      childElement: PropTypes.node,
    }),
  ).isRequired,
  renderOwnedLink: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
    PropTypes.func,
  ]),
  storeCreditBalance: PropTypes.number,
  tkrsGCBalance: PropTypes.number,
};

export default YourRegistryHeaderBase;
